import { memo, type ReactNode } from "react";
import CenterContents from "~/common/components/CenterContents";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import classNames from "classnames";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";

export interface LinearTileProps {
  className?: string;
  title?: ReactNode;
  link?: string;
  openLinkInNewTab?: boolean;
  description?: string;
  info?: string;
  isStandard?: boolean;
  forceStandardSpace?: boolean;
  imageUrl?: string;
  isDisabled?: boolean;
  isPicked?: boolean;
  selectable?: boolean;
  onClickHandler?: () => void;
  children?: ReactNode;
  error?: boolean;
}

const LinearTile = (props: LinearTileProps) => {
  const {
    className,
    description,
    title,
    isDisabled,
    isPicked,
    imageUrl,
    info,
    onClickHandler,
    link,
    openLinkInNewTab,
    isStandard,
    forceStandardSpace,
    children,
    selectable = true,
    error,
  } = props;

  const titleElement =
    typeof title === "string" ? (
      <LanguageAwareExternalContent content={title} className={"scha__lc__external-label"} />
    ) : (
      title
    );

  return (
    <Card
      className={classNames("scha__lc__tile", className)}
      onClick={onClickHandler}
      disabled={isDisabled}
      active={isPicked}
      disableHover={isDisabled}
      as={link ? "a" : "button"}
      href={link}
      target={link && openLinkInNewTab ? "_blank" : undefined}
      error={error}
    >
      {imageUrl && <LCTileImage imageUrl={imageUrl} />}

      {selectable && <CheckedCircle className={"scha__lc__tile__checkmark"} checked={isPicked} />}

      <div className="scha__lc__card__contents">
        <p className="scha__lc__card__headline">
          {titleElement}
          <LinearInfo info={info} popupClassName={"scha__lc__external-content--shaft"} />
        </p>

        {description && (
          <p className="scha__lc__card__subheadline">
            <LanguageAwareExternalContent content={description} className={"scha__lc__external-label"} />
          </p>
        )}

        {(isStandard || forceStandardSpace) && (
          <div className={!isStandard ? "v-hidden" : "mt-auto"}>
            <StandardStarComponent />
          </div>
        )}

        {children}
      </div>
    </Card>
  );
};

export default LinearTile;

export const StandardStarComponent = memo(() => {
  const { t } = useTranslation();
  return (
    <div className="scha__lc__card__standard">
      <span className="scha-icon-star" />
      <span>{t("configurator.standard")}</span>
    </div>
  );
});

export const LCTileImage = ({ imageUrl, className }: { imageUrl: string; className?: string }) => {
  const { t } = useXmlConfiguratorTranslations();
  imageUrl = t ? t(imageUrl) : imageUrl;
  return (
    <div className={classNames("scha__lc__card__image", className)}>
      <CenterContents>
        <img src={imageUrl} />
      </CenterContents>
    </div>
  );
};
